import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../firebaseConfig';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --bg:          #0a1628;
    --bg-card:     #0f1e35;
    --teal:        #00adb5;
    --teal-dim:    rgba(0,173,181,0.12);
    --teal-border: rgba(0,173,181,0.22);
    --white:       #f0f4f8;
    --muted:       rgba(240,244,248,0.45);
    --transition:  0.22s cubic-bezier(0.4,0,0.2,1);
  }

  .anunciar-page {
    min-height: 100vh;
    background: linear-gradient(150deg, #0d2240 0%, #0a1628 40%, #080f1c 100%);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 48px 20px 80px;
    font-family: 'DM Sans', sans-serif;
  }

  .anunciar-card {
    background: var(--bg-card);
    border: 1px solid var(--teal-border);
    border-radius: 16px;
    padding: 40px 36px;
    width: 100%;
    max-width: 560px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.4);
  }

  @media (max-width: 600px) {
    .anunciar-card { padding: 28px 20px; }
  }

  .anunciar-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--teal);
    margin-bottom: 14px;
  }
  .anunciar-badge-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--teal);
    animation: pulse-dot 1.8s ease-in-out infinite;
  }
  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.7); }
  }

  .anunciar-title {
    font-size: 22px;
    font-weight: 600;
    color: var(--white);
    margin-bottom: 6px;
  }
  .anunciar-sub {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.6;
    margin-bottom: 28px;
  }

  .anunciar-label {
    display: block;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 8px;
  }

  .anunciar-textarea {
    background: rgba(0,173,181,0.07);
    border: 1px solid var(--teal-border);
    border-radius: 8px;
    padding: 14px;
    font-family: 'DM Mono', monospace;
    font-size: 14px;
    color: var(--white);
    outline: none;
    width: 100%;
    min-height: 120px;
    resize: vertical;
    transition: border-color var(--transition), background var(--transition), box-shadow var(--transition);
    line-height: 1.6;
  }
  .anunciar-textarea::placeholder { color: rgba(240,244,248,0.2); font-family: 'DM Sans', sans-serif; }
  .anunciar-textarea:focus {
    border-color: var(--teal);
    background: rgba(0,173,181,0.12);
    box-shadow: 0 0 0 3px rgba(0,173,181,0.08);
  }
  .anunciar-textarea.error { border-color: rgba(255,87,34,0.6); }

  .anunciar-select-wrap { position: relative; margin-top: 20px; }
  .anunciar-select-wrap::after {
    content: '';
    position: absolute;
    right: 14px; top: 50%;
    transform: translateY(-50%);
    width: 0; height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid var(--teal);
    pointer-events: none;
  }
  .anunciar-select {
    background: rgba(0,173,181,0.07);
    border: 1px solid var(--teal-border);
    border-radius: 8px;
    padding: 11px 38px 11px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: var(--white);
    outline: none;
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    transition: border-color var(--transition), background var(--transition), box-shadow var(--transition);
    cursor: pointer;
  }
  .anunciar-select:focus {
    border-color: var(--teal);
    background: rgba(0,173,181,0.12);
    box-shadow: 0 0 0 3px rgba(0,173,181,0.08);
  }
  .anunciar-select.error { border-color: rgba(255,87,34,0.6); }
  .anunciar-select option { background: #0f1e35; color: var(--white); }
  .anunciar-select option:disabled { color: var(--muted); }

  .anunciar-field-error {
    font-size: 12px;
    color: #ff7043;
    margin-top: 6px;
    display: block;
  }
  .anunciar-hint {
    font-size: 11.5px;
    color: rgba(240,244,248,0.28);
    margin-top: 6px;
    line-height: 1.5;
    display: block;
  }

  /* Preview dos domínios detectados */
  .anunciar-preview {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .anunciar-preview-tag {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: var(--teal);
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 5px;
    padding: 3px 10px;
  }
  .anunciar-preview-count {
    font-size: 11.5px;
    color: var(--muted);
    margin-top: 6px;
    display: block;
  }

  .anunciar-divider {
    height: 1px;
    background: rgba(255,255,255,0.06);
    margin: 24px 0;
  }

  /* Usuário */
  .anunciar-user-info {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(0,173,181,0.06);
    border: 1px solid var(--teal-border);
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 24px;
  }
  .anunciar-user-avatar {
    width: 34px; height: 34px;
    border-radius: 50%;
    background: rgba(0,173,181,0.2);
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 600;
    color: var(--teal);
    flex-shrink: 0;
    border: 1px solid var(--teal-border);
    overflow: hidden;
  }
  .anunciar-user-avatar img {
    width: 34px; height: 34px;
    border-radius: 50%; object-fit: cover;
  }
  .anunciar-user-text { flex: 1; min-width: 0; }
  .anunciar-user-name {
    font-size: 13px; font-weight: 600; color: var(--white);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .anunciar-user-email {
    font-size: 11.5px; color: var(--muted);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  /* Status */
  .anunciar-status-preview {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255,193,7,0.07);
    border: 1px solid rgba(255,193,7,0.2);
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 24px;
    font-size: 12.5px;
    color: rgba(255,193,7,0.85);
  }
  .anunciar-status-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: rgba(255,193,7,0.8);
    flex-shrink: 0;
  }

  .anunciar-alert {
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 20px;
  }
  .anunciar-alert--error {
    background: rgba(255,87,34,0.1);
    border: 1px solid rgba(255,87,34,0.3);
    color: #ff7043;
  }

  /* Ações */
  .anunciar-actions {
    display: flex;
    gap: 12px;
    margin-top: 8px;
  }
  .anunciar-btn-secondary {
    flex: 1;
    background: transparent;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px;
    color: var(--muted);
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 500;
    padding: 12px;
    cursor: pointer;
    transition: border-color var(--transition), color var(--transition), background var(--transition);
  }
  .anunciar-btn-secondary:hover {
    border-color: rgba(255,255,255,0.25);
    color: var(--white);
    background: rgba(255,255,255,0.04);
  }
  .anunciar-btn-primary {
    flex: 2;
    background: var(--teal);
    border: none;
    border-radius: 8px;
    color: #fff;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600;
    padding: 12px;
    cursor: pointer;
    transition: background var(--transition), box-shadow var(--transition), transform var(--transition);
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .anunciar-btn-primary:hover:not(:disabled) {
    background: #009aa2;
    box-shadow: 0 4px 16px rgba(0,173,181,0.4);
    transform: translateY(-1px);
  }
  .anunciar-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

  .anunciar-spinner {
    width: 16px; height: 16px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Não logado */
  .anunciar-unauth {
    display: flex; flex-direction: column;
    align-items: center; text-align: center; gap: 16px; padding: 20px 0 8px;
  }
  .anunciar-unauth-icon { font-size: 40px; opacity: 0.4; }
  .anunciar-unauth-title { font-size: 17px; font-weight: 600; color: var(--white); }
  .anunciar-unauth-sub { font-size: 13px; color: var(--muted); line-height: 1.6; max-width: 320px; }
  .anunciar-unauth-btn {
    background: var(--teal); border: none; border-radius: 8px;
    color: #fff; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600; padding: 12px 28px;
    cursor: pointer; margin-top: 4px;
    transition: background var(--transition), box-shadow var(--transition);
  }
  .anunciar-unauth-btn:hover { background: #009aa2; box-shadow: 0 4px 16px rgba(0,173,181,0.4); }

  /* Sucesso */
  .anunciar-success {
    display: flex; flex-direction: column;
    align-items: center; text-align: center; gap: 14px; padding: 16px 0;
  }
  .anunciar-success-icon {
    width: 64px; height: 64px; border-radius: 50%;
    background: rgba(0,173,181,0.12); border: 1px solid var(--teal-border);
    display: flex; align-items: center; justify-content: center; font-size: 28px;
  }
  .anunciar-success-title { font-size: 20px; font-weight: 600; color: var(--white); }
  .anunciar-success-sub { font-size: 13px; color: var(--muted); line-height: 1.6; max-width: 360px; }
  .anunciar-success-list {
    display: flex; flex-wrap: wrap; gap: 6px;
    justify-content: center; max-width: 420px;
  }
  .anunciar-success-domain {
    font-family: 'DM Mono', monospace;
    font-size: 13px; color: var(--teal);
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 5px; padding: 5px 12px;
  }
  .anunciar-success-actions {
    display: flex; gap: 10px; margin-top: 8px;
    flex-wrap: wrap; justify-content: center;
  }
  .anunciar-success-btn {
    background: transparent; border: 1px solid var(--teal-border);
    border-radius: 8px; color: var(--teal);
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 500;
    padding: 10px 20px; cursor: pointer;
    transition: background var(--transition), border-color var(--transition);
  }
  .anunciar-success-btn:hover { background: var(--teal-dim); border-color: var(--teal); }
  .anunciar-success-btn--primary {
    background: var(--teal); border-color: var(--teal); color: #fff;
  }
  .anunciar-success-btn--primary:hover { background: #009aa2; }
`;

const SETORES_FALLBACK = [
  'Automotivo e Mobilidade',
  'Domínios Premium e Marca',
  'Eventos, Lazer e Cultura',
  'Gastronomia e Alimentação',
  'Moda, Beleza e Cosmético',
  'Moda, Beleza e Estilo',
  'Negócios, Serviços e Indústria',
  'Saúde, Bem-Estar e Odonto',
  'Tecnologia, Digital e Finanças',
];

// Extrai lista de domínios a partir do texto do textarea
const parseDominios = (text) =>
  text
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);

export default function Anunciar() {
  const navigate = useNavigate();
  const auth     = getAuth();

  const [user, setUser]           = useState(undefined);
  const [setores, setSetores]     = useState(SETORES_FALLBACK);
  const [texto, setTexto]         = useState('');
  const [setor, setSetor]         = useState('');
  const [errors, setErrors]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [submitted, setSubmitted]     = useState(false);
  const [submittedList, setSubmittedList] = useState([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u ?? null));
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchSetores = async () => {
      try {
        const snap = await getDocs(collection(firestore, 'dominios'));
        const set  = new Set();
        snap.forEach((d) => { const s = d.data().setor?.trim(); if (s) set.add(s); });
        if (set.size > 0) setSetores(Array.from(set).sort());
      } catch { /* usa fallback */ }
    };
    fetchSetores();
  }, []);

  const dominiosList = parseDominios(texto);

  const validate = () => {
    const errs = {};
    if (!texto.trim()) {
      errs.texto = 'Informe ao menos um domínio.';
    } else if (dominiosList.length === 0) {
      errs.texto = 'Nenhum domínio válido encontrado.';
    }
    if (!setor) errs.setor = 'Selecione um setor.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const titular = user.displayName || user.email.split('@')[0];
      const contato = user.email;

      // Verifica duplicatas: busca todos os nomes já existentes na coleção
      const snap = await getDocs(collection(firestore, 'dominios'));
      const existentes = new Set(snap.docs.map((d) => d.data().nome?.trim().toLowerCase()).filter(Boolean));

      const duplicados = dominiosList.filter((nome) => existentes.has(nome));
      const novos      = dominiosList.filter((nome) => !existentes.has(nome));

      if (duplicados.length > 0 && novos.length === 0) {
        setGlobalError(
          `${duplicados.length === 1 ? 'Este domínio já está' : 'Estes domínios já estão'} cadastrado(s): ${duplicados.join(', ')}`
        );
        setSubmitting(false);
        return;
      }

      if (novos.length === 0) {
        setGlobalError('Nenhum domínio novo para cadastrar.');
        setSubmitting(false);
        return;
      }

      // Grava apenas os domínios novos
      await Promise.all(
        novos.map((nome) =>
          addDoc(collection(firestore, 'dominios'), {
            nome,
            setor,
            status:  'Em análise',
            contato,
            titular,
            data:    serverTimestamp(),
          })
        )
      );

      // Avisa sobre duplicados ignorados se houver mistura
      if (duplicados.length > 0) {
        setGlobalError(
          `Ignorado(s) por já existir(em): ${duplicados.join(', ')}`
        );
      }

      setSubmittedList(novos);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setGlobalError('Erro ao enviar. Tente novamente em instantes.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNovo = () => {
    setTexto('');
    setSetor('');
    setErrors({});
    setGlobalError('');
    setSubmitted(false);
    setSubmittedList([]);
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Usuário';

  // ── Loading auth ──
  if (user === undefined) {
    return (
      <>
        <style>{styles}</style>
        <div className="anunciar-page">
          <div className="anunciar-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <div className="anunciar-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
              <span style={{ color: 'var(--muted)', fontSize: 13 }}>Verificando acesso...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Não logado ──
  if (!user) {
    return (
      <>
        <style>{styles}</style>
        <div className="anunciar-page">
          <div className="anunciar-card">
            <div className="anunciar-badge"><span className="anunciar-badge-dot" />Anunciar domínio</div>
            <div className="anunciar-title">Anuncie seu domínio</div>
            <div className="anunciar-unauth">
              <div className="anunciar-unauth-icon">🔒</div>
              <div className="anunciar-unauth-title">Acesso restrito</div>
              <p className="anunciar-unauth-sub">Para anunciar um domínio na plataforma VEDO, você precisa estar logado.</p>
              <button className="anunciar-unauth-btn" onClick={() => navigate('/')}>Entrar / Criar conta</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Sucesso ──
  if (submitted) {
    return (
      <>
        <style>{styles}</style>
        <div className="anunciar-page">
          <div className="anunciar-card">
            <div className="anunciar-success">
              <div className="anunciar-success-icon">✅</div>
              <div className="anunciar-success-title">
                {submittedList.length === 1 ? 'Domínio enviado!' : `${submittedList.length} domínios enviados!`}
              </div>
              <p className="anunciar-success-sub">
                {submittedList.length === 1
                  ? 'Seu domínio foi cadastrado e está aguardando análise.'
                  : 'Seus domínios foram cadastrados e estão aguardando análise.'
                } Entraremos em contato pelo e-mail{' '}
                <strong style={{ color: 'var(--white)' }}>{user.email}</strong>.
              </p>
              <div className="anunciar-success-list">
                {submittedList.map((d) => (
                  <div key={d} className="anunciar-success-domain">{d}</div>
                ))}
              </div>
              {globalError && (
                <p style={{ fontSize: 12, color: 'rgba(255,193,7,0.8)', marginTop: 4, textAlign: 'center' }}>
                  ⚠️ {globalError}
                </p>
              )}
              <p style={{ fontSize: 12, color: 'rgba(255,193,7,0.8)', marginTop: 4 }}>
                Status: <strong>Em análise</strong>
              </p>
              <div className="anunciar-success-actions">
                <button className="anunciar-success-btn" onClick={handleNovo}>Anunciar mais</button>
                <button className="anunciar-success-btn anunciar-success-btn--primary" onClick={() => navigate('/')}>Ver catálogo</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Formulário ──
  return (
    <>
      <style>{styles}</style>
      <div className="anunciar-page">
        <div className="anunciar-card">

          <div className="anunciar-badge"><span className="anunciar-badge-dot" />Anunciar domínio</div>
          <div className="anunciar-title">Cadastre seus domínios</div>
          <p className="anunciar-sub">
            Informe um ou mais domínios separados por vírgula. Nossa equipe analisará e entrará em contato.
          </p>

          {/* Usuário */}
          <div className="anunciar-user-info">
            <div className="anunciar-user-avatar">
              {user.photoURL
                ? <img src={user.photoURL} alt={displayName} referrerPolicy="no-referrer" />
                : displayName[0].toUpperCase()
              }
            </div>
            <div className="anunciar-user-text">
              <div className="anunciar-user-name">{displayName}</div>
              <div className="anunciar-user-email">{user.email}</div>
            </div>
          </div>

          {/* Status */}
          <div className="anunciar-status-preview">
            <div className="anunciar-status-dot" />
            Todos os domínios serão cadastrados com status <strong style={{ marginLeft: 4 }}>Em análise</strong>
          </div>

          {globalError && <div className="anunciar-alert anunciar-alert--error">{globalError}</div>}

          <form onSubmit={handleSubmit} noValidate>

            {/* Domínios */}
            <div>
              <label className="anunciar-label">Domínios</label>
              <textarea
                className={`anunciar-textarea${errors.texto ? ' error' : ''}`}
                placeholder={'meudominio.com.br, outrodominio.com.br, mais-um.com.br'}
                value={texto}
                onChange={(e) => { setTexto(e.target.value); setErrors((p) => ({ ...p, texto: '' })); }}
              />
              {errors.texto
                ? <span className="anunciar-field-error">{errors.texto}</span>
                : <span className="anunciar-hint">Separe múltiplos domínios por vírgula.</span>
              }

              {/* Preview em tempo real */}
              {dominiosList.length > 0 && (
                <>
                  <span className="anunciar-preview-count">
                    {dominiosList.length} {dominiosList.length === 1 ? 'domínio detectado' : 'domínios detectados'}:
                  </span>
                  <div className="anunciar-preview">
                    {dominiosList.map((d, i) => (
                      <span key={i} className="anunciar-preview-tag">{d}</span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Setor */}
            <div className="anunciar-select-wrap">
              <label className="anunciar-label">Setor</label>
              <select
                className={`anunciar-select${errors.setor ? ' error' : ''}`}
                value={setor}
                onChange={(e) => { setSetor(e.target.value); setErrors((p) => ({ ...p, setor: '' })); }}
              >
                <option value="" disabled>Selecione um setor...</option>
                {setores.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.setor && <span className="anunciar-field-error">{errors.setor}</span>}
            </div>

            <div className="anunciar-divider" />

            <div className="anunciar-actions">
              <button type="button" className="anunciar-btn-secondary" onClick={() => navigate('/')} disabled={submitting}>
                Cancelar
              </button>
              <button type="submit" className="anunciar-btn-primary" disabled={submitting}>
                {submitting
                  ? <><div className="anunciar-spinner" /> Enviando...</>
                  : dominiosList.length > 1
                    ? `Enviar ${dominiosList.length} domínios`
                    : 'Enviar para análise'
                }
              </button>
            </div>

          </form>
        </div>
      </div>
    </>
  );
}
